import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import classes from "./Registration.module.css";
import axios from "../../../axios";

const RegistrationInvite = () => {
  const [searchParams] = useSearchParams();
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);

  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [inviteData, setInviteData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setTokenError("Invalid invitation link. No token provided.");
      setValidatingToken(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await axios.get("/registration/validate-invite-token", {
          params: { token }
        });

        setInviteData({
          email: response.data.email,
          organizationName: response.data.organizationName,
          organizationId: response.data.organizationId,
          roles: Array.isArray(response.data.roles) ? response.data.roles : []
        });
        setTokenError(null);
      } catch (err) {
        let message = "Invitation link is invalid or has expired.";
        if (err.response?.data?.message) {
          message = err.response.data.message;
        }
        setTokenError(message);
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const validateForm = () => {
    const password = passwordRef.current?.value || "";
    const confirmPassword = confirmPasswordRef.current?.value || "";

    if (!password) {
      setError("Please enter a password");
      return false;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!inviteData) return;

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    const password = passwordRef.current.value;
    const firstName = firstNameRef.current?.value || "";
    const lastName = lastNameRef.current?.value || "";

    try {
      await axios.post("/registration/register", {
        email: inviteData.email,
        password,
        firstName,
        lastName,
        organizationId: inviteData.organizationId,
        redirectUrl: window.location.origin,
        roles: inviteData.roles || []
      });

      setSuccess(true);
    } catch (err) {
      let message = "Registration failed. Please try again.";
      if (err.response?.data?.message) {
        message = err.response.data.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.messageContainer}>
          <div className={classes.spinner}></div>
          <h1 className={classes.title}>Validating Invitation</h1>
          <p className={classes.message}>
            Please wait while we validate your invitation link...
          </p>
        </div>
      </div>
    );
  }

  if (tokenError || !inviteData) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.messageContainer}>
          <h1 className={classes.title}>Invitation Problem</h1>
          <p className={classes.message}>{tokenError || "Invitation information is missing."}</p>
          <p className={classes.submessage}>
            Please check that you used the latest invitation link or request a new one.
          </p>
          <Link to="/" className={classes.backLink}>
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.messageContainer}>
          <div className={classes.successIcon}>✓</div>
          <h1 className={classes.title}>You're In!</h1>
          <p className={classes.message}>
            Your account has been created and you have joined{" "}
            <strong>{inviteData.organizationName}</strong>.
          </p>
          <p className={classes.submessage}>
            You can now log in to your account using your email and password.
          </p>
          <Link to="/" className={classes.backLink}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.pageContainer}>
      <div className={classes.formContainer}>
        <h1 className={classes.title}>Accept Invitation</h1>
        <p className={classes.subtitle}>
          Join organization <strong>{inviteData.organizationName}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          <div className={classes.row}>
            <input
              type="text"
              placeholder="First Name"
              className={classes.inputField}
              ref={firstNameRef}
              disabled={loading}
            />
            <input
              type="text"
              placeholder="Last Name"
              className={classes.inputField}
              ref={lastNameRef}
              disabled={loading}
            />
          </div>

          <input
            type="email"
            className={classes.inputField}
            value={inviteData.email}
            disabled
          />

          <input
            type="password"
            placeholder="Password *"
            className={classes.inputField}
            ref={passwordRef}
            disabled={loading}
            autoComplete="new-password"
          />

          <input
            type="password"
            placeholder="Confirm Password *"
            className={classes.inputField}
            ref={confirmPasswordRef}
            disabled={loading}
            autoComplete="new-password"
          />

          {error && (
            <div className={classes.errorContainer}>
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            className={classes.submitButton}
            disabled={loading}
          >
            {loading ? <span className={classes.spinner}></span> : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegistrationInvite;


